import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bug, Shield, Brain, Wrench } from "lucide-react";
import axios from "axios";

const METRIC_ICONS = {
  "Bug Detection Accuracy": Bug,
  "Test Coverage": Shield,
  "Reasoning Score": Brain,
  "Tool Usage Efficiency": Wrench,
};

const METRIC_COLORS = {
  "Bug Detection Accuracy": "arena-blue",
  "Test Coverage": "arena-green",
  "Reasoning Score": "amber-400",
  "Tool Usage Efficiency": "violet-400",
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export const BenchmarkingSystem = ({ apiUrl }) => {
  const [benchmarks, setBenchmarks] = useState([]);

  useEffect(() => {
    const fetchBenchmarks = async () => {
      try {
        const res = await axios.get(`${apiUrl}/benchmarks`);
        setBenchmarks(res.data);
      } catch (err) {
        console.error("Failed to fetch benchmarks:", err);
      }
    };
    fetchBenchmarks();
  }, [apiUrl]);

  return (
    <section
      id="benchmarks"
      data-testid="benchmarks-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <span className="text-xs font-mono text-arena-green uppercase tracking-[0.2em]">
            Metrics
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mt-3 tracking-tight">
            Benchmarking System
          </h2>
          <p className="mt-4 text-sm font-mono text-zinc-500 max-w-lg">
            Agents are evaluated across four key dimensions. Scores are computed from real-world testing scenarios.
          </p>
        </motion.div>

        {/* Metrics grid - High Density */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {benchmarks.map((metric) => {
            const Icon = METRIC_ICONS[metric.name] || Bug;
            const colorKey = METRIC_COLORS[metric.name] || "arena-blue";
            const progressColor =
              colorKey === "arena-blue" ? "bg-blue-500" :
              colorKey === "arena-green" ? "bg-emerald-500" :
              colorKey === "amber-400" ? "bg-amber-400" :
              "bg-violet-400";
            const iconColor =
              colorKey === "arena-blue" ? "text-blue-400" :
              colorKey === "arena-green" ? "text-emerald-400" :
              colorKey === "amber-400" ? "text-amber-400" :
              "text-violet-400";

            return (
              <motion.div
                key={metric.id}
                variants={itemVariants}
                data-testid={`benchmark-card-${metric.name.toLowerCase().replace(/\s/g, "-")}`}
                className="group p-6 bg-arena-surface border border-white/[0.06] rounded-md hover:border-white/[0.12] transition-colors"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Icon className={`w-5 h-5 ${iconColor}`} />
                  <span className="text-xs font-mono text-zinc-500 uppercase tracking-wider">
                    {metric.name}
                  </span>
                </div>

                {/* Score */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading font-bold text-3xl text-white">
                      {metric.top_score}
                    </span>
                    <span className="text-xs font-mono text-zinc-600">
                      / 100
                    </span>
                  </div>
                  <div className="text-xs font-mono text-zinc-600 mt-1">
                    Top Score
                  </div>
                </div>

                {/* Progress bar */}
                <div className="w-full h-1 bg-white/[0.06] rounded-full mb-4">
                  <div
                    className={`h-full rounded-full ${progressColor}`}
                    style={{ width: `${metric.top_score}%` }}
                  />
                </div>

                {/* Meta */}
                <div className="flex justify-between text-xs font-mono">
                  <span className="text-zinc-600">
                    Avg: {metric.avg_score}
                  </span>
                  <span className="text-zinc-600">
                    {metric.total_submissions.toLocaleString()} runs
                  </span>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
